import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';

export default function BandSelector() {
  const { t } = useTranslation();
  const { profiles, activeProfileId, setActiveProfile } =
    useProfileContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = profiles.find((p) => p.id === activeProfileId);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = async (id: string) => {
    const p = profiles.find((x) => x.id === id);
    setOpen(false);
    if (!p) return;
    // Navigate DIRECTLY to the new band's page. The App.tsx effect syncs the
    // active profile from the URL slug (/band/:slug → setActiveProfile), so
    // the switch is handled by the app's own mechanism — no manual profile
    // set, no race with the URL-sync effect (this fixes the "must click
    // twice: first click bounces back to the current band" bug). (2026-08-09)
    // P2e — the target is the band's contextual HOME (Day-of if a show is
    // today, else Atlas), same mechanism, same sync, only the landing moved.
    if (p.slug) {
      navigate(`/band/${p.slug}/home`);
    } else {
      await setActiveProfile(id);
      navigate('/tour/shows');
    }
  };

  return (
    <div className="profile-selector" ref={ref}>
      <button
        className="profile-selector-trigger"
        onClick={() => setOpen(!open)}
        title={active?.name || t('bandSelector.selectBand')}
      >
        {/* Band name + chevron only (band logo removed per user request). */}
        <span className="profile-trigger-row">
          <span className="profile-trigger-name">
            {active?.name || t('bandSelector.noBand')}
          </span>
          <svg
            className={`profile-chevron ${open ? 'open' : ''}`}
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M4 6l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="profile-selector-dropdown">
          {profiles.map((p) => (
            <button
              key={p.id}
              className={`profile-dropdown-item ${
                p.id === activeProfileId ? 'active' : ''
              }`}
              onClick={() => handleSelect(p.id)}
            >
              <div className="profile-dropdown-info">
                <span className="profile-dropdown-name">{p.name}</span>
                <span className="profile-dropdown-meta">
                  {t('bandSelector.updated', { date: new Date(p.lastModified).toLocaleDateString() })}
                </span>
              </div>
              {p.id === activeProfileId && (
                <svg
                  className="profile-check"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13 4L6 12l-3-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}

          <div className="profile-dropdown-divider" />

          <button
            className="profile-dropdown-item manage"
            onClick={() => {
              setOpen(false);
              navigate('/bands');
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            {t('bandSelector.manageBands')}
          </button>
        </div>
      )}
    </div>
  );
}
